// types
import { ApolloClient } from '@apollo/client'
import { AttachmentType } from 'components/FormElements/DocumentOrLink/Risk'
import { OfflineStorageService } from './offlineStorage'
import { IFieldValue, IFieldValueMapItem, IGenerateUploadUrlsResponse, IRiskResponse } from 'types/graphql'
// graphql
import { RISK_CREATE_MUTATION } from 'graphql/risk/mutations'
import { GENERATE_UPLOAD_URL_MUTATION } from 'graphql/assets/mutations'
import { FIELD_VALUE_UPSERT_MUTATION } from 'graphql/objects/mutations'
// utils
import { omit } from 'lodash'
import { base64ToFile } from 'utils/upload'
import { decodeJwtExp } from 'providers/SessionProvider/utils'
import { getAccessToken } from 'lib/auth'

type RiskCreateResponse = { riskCreate: IRiskResponse }
type GenerateUrlResponse = { generateUploadUrls: IGenerateUploadUrlsResponse }
type FieldValueUpsertResponse = { fieldValueUpsert: IFieldValue[] }

export class OfflineSyncService {
  private apolloClient: ApolloClient
  private storage: OfflineStorageService
  private isSyncing = false

  constructor(apolloClient: ApolloClient) {
    this.apolloClient = apolloClient
    this.storage = OfflineStorageService.getInstance()
  }

  async imageUpload(variables: {
    file: string
    fileName: string
    assetType: string
    contentType: string
    entityPrefix: string
  }) {
    try {
      const file = base64ToFile(variables.file, variables.fileName)

      const uploadResult = await this.apolloClient.mutate<GenerateUrlResponse>({
        mutation: GENERATE_UPLOAD_URL_MUTATION,
        variables: {
          file: {
            assetType: variables.assetType,
            entityPrefix: variables.entityPrefix,
            contentType: variables.contentType
          }
        }
      })

      const { downloadUrl, uploadUrl, uploadFormData } = uploadResult.data.generateUploadUrls

      const form = new FormData()

      Object.entries(uploadFormData).forEach(([key, value]) => {
        form.append(key, value as string)
      })

      form.append('file', file)

      await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': variables.contentType,
          'Access-Control-Request-Headers': 'Content-Type'
        },
        body: form
      }).catch((err) => console.warn('[OfflineSyncService.imageUpload]', err))

      return { name: variables.fileName, url: downloadUrl, type: AttachmentType.Image }
    } catch (error) {
      console.error('Failed to upload image:', error)
    }
  }

  async syncPendingMutations(riskId?: string) {
    // Re-entrancy guard — prevent concurrent sync runs
    if (this.isSyncing) {
      return null
    }

    // Network guard
    if (!navigator.onLine) {
      console.warn('[OfflineSyncService] syncPendingMutations called while offline — aborting')
      return null
    }

    // Token presence guard
    const token = getAccessToken()

    if (!token) {
      console.warn('[OfflineSyncService] No access token — cannot sync')
      return null
    }

    // Token expiry guard — session manager should have refreshed by now, but belt-and-suspenders
    const exp = decodeJwtExp(token)

    if (exp && exp < Date.now()) {
      console.warn('[OfflineSyncService] Token is expired — aborting sync')
      return null
    }

    this.isSyncing = true

    try {
      return await this.runSync(riskId)
    } finally {
      this.isSyncing = false
    }
  }

  private async runSync(riskId?: string): Promise<string | null> {
    const mutations = this.storage.getMutationsInOrder()

    let realRiskId = null

    for (const mutation of mutations) {
      try {
        if (mutation.type === 'riskCreate') {
          const result = await this.apolloClient.mutate<RiskCreateResponse>({
            mutation: RISK_CREATE_MUTATION,
            variables: mutation.variables
          })

          const realId: string = result.data.riskCreate.data.id
          const fieldValueMap: IFieldValueMapItem[] = result.data.riskCreate.data.fieldValueMap

          if (mutation.tempId) {
            this.storage.updateTempIdMapping(mutation.tempId, realId)
          }

          if (mutation.tempId === riskId) {
            realRiskId = realId
          }

          if (fieldValueMap.length) {
            mutations.forEach(({ variables }) => {
              const fieldValue = fieldValueMap.find((item) => item.fieldId === variables.input[0]?.fieldId)

              if (fieldValue) {
                this.storage.updateFieldValueMapping(fieldValue.fieldId, fieldValue.fieldValueId)
              }
            })
          }

          this.storage.removeMutation([mutation.id])
        } else if (mutation.type === 'fieldUpsert') {
          const tempIdMappings = this.storage.getTempIdMappings()
          const fieldValueMappings = this.storage.getFieldValueMappings()

          const variables = { ...mutation.variables }

          const { fieldId, fieldValueId, parentFieldValueId } = variables.input[0]

          if (variables.entityId && tempIdMappings[variables.entityId]) {
            variables.entityId = tempIdMappings[variables.entityId]
          }

          if (fieldId && fieldValueMappings[fieldId]) {
            variables.input = [
              {
                ...omit(variables.input[0], 'fieldId'),
                fieldValueId: fieldValueMappings[fieldId]
              }
            ]
          }

          if (fieldValueId && fieldValueMappings[fieldValueId]) {
            variables.input = [
              {
                ...variables.input[0],
                fieldValueId: fieldValueMappings[fieldValueId]
              }
            ]
          }

          if (parentFieldValueId && fieldValueMappings[parentFieldValueId]) {
            variables.input = [
              {
                ...variables.input[0],
                parentFieldValueId: fieldValueMappings[parentFieldValueId]
              }
            ]
          }

          if (variables.input[0].value?.file) {
            const fieldValue = await this.imageUpload(variables.input[0].value)

            variables.input = [
              {
                ...variables.input[0],
                value: fieldValue
              }
            ]
          }

          const result = await this.apolloClient.mutate<FieldValueUpsertResponse>({
            mutation: FIELD_VALUE_UPSERT_MUTATION,
            variables: variables
          })

          const realFieldValueId = result.data.fieldValueUpsert[0].id

          if (mutation.tempId && realFieldValueId) {
            this.storage.updateFieldValueMapping(mutation.tempId, realFieldValueId)
          }

          this.storage.removeMutation([mutation.id])
        }
      } catch (error) {
        console.error('Failed to sync mutation:', mutation, error)
        break
      }
    }

    return realRiskId
  }
}
